import { getCSRFToken } from './utils'

export const LOGOUT_REQUEST_TIMEOUT_MS = 5000

// Bounds a call so a logout handler's finally block (which clears client state) always runs, even
// when the request hangs or hits one of api.ts's deliberately never-settling branches. Issue #857
// (item 5): takes an optional onTimeout hook, fired synchronously with the rejection below, so a
// caller can cancel whatever is still in flight instead of leaving it running unobserved.
export function withTimeout<T>(promise: Promise<T>, ms: number = LOGOUT_REQUEST_TIMEOUT_MS, onTimeout?: () => void): Promise<T> {
	return new Promise<T>((resolve, reject) => {
		const timer = setTimeout(() => {
			onTimeout?.()
			reject(new Error('Request timed out'))
		}, ms)
		promise.then(
			(value) => {
				clearTimeout(timer)
				resolve(value)
			},
			(error) => {
				clearTimeout(timer)
				reject(error)
			}
		)
	})
}

// The CSRF fetch runs inside the timeout too: awaiting it as an argument would leave it unbounded.
export async function performLogoutRequest(publicApi: {
	post: (path: string, body: any, options?: { signal?: AbortSignal }) => Promise<any>
}): Promise<void> {
	// Issue #857 (item 5): previously, once this function's timeout rejected, the CSRF fetch
	// and/or logout POST it was racing kept running in the background to completion - wasted
	// work on a connection that is, by definition, being abandoned (the user is logging out),
	// and liable to resolve/reject later with no one left listening. onTimeout below cancels
	// whichever of the two is still outstanding the moment the timeout actually fires.
	const controller = new AbortController()
	await withTimeout(
		(async () => {
			const csrf = await getCSRFToken(controller.signal)
			await publicApi.post('/user/logout', { _csrf: csrf }, { signal: controller.signal })
		})(),
		LOGOUT_REQUEST_TIMEOUT_MS,
		() => controller.abort()
	)
}
